import React from 'react'
import { useIntl } from 'react-intl'
import { Box, Row } from '../../legacy-design-system/form'
import { TextInput } from '../../legacy-design-system/form-text'
import { css, cx } from '@emotion/css'
import { Label } from '../../legacy-design-system/form-label'
import { BasicTooltipContent } from '../../legacy-design-system/basic-tooltip-content'
import { GetFormFieldPropsFn } from '../../legacy-design-system/form/form-validator'
import { IPCIDR } from '../source-names/ipcidr'
import { DatePickerDropdown } from '../../legacy-design-system/date-picker/date-picker-dropdown'
import { DatePicker } from '../../legacy-design-system/date-picker/date-picker'
import { paragraphTextCss } from '../../legacy-design-system/text'
import { DateOption } from '../../legacy-design-system/date-picker/shared'
import { calculateIpRange } from './calculate-ip-range'

export const ipPolicyNameDataTestID = 'text-input-ip-policy-name'
export const ipBlockDataTestID = 'text-input-ip-block'
export const ipPolicyExpirationDataTestID = 'dropdown-ip-expiration'
export const ipRangeDataTestID = 'paragraph-ip-range'

interface BlockIpFormProps {
  name?: string
  ip?: string
  expiration: DateOption
  expirationTime?: Date
  isUniqueName: boolean
  ipMinRange?: string
  ipMaxRange?: string
  setName: (name: string) => void
  setIp: (name: string) => void
  setExpiration: (expiration: DateOption) => void
  setExpirationTime: (expirationTime?: Date) => void
  setIsUniqueName: (isUniqueName: boolean) => void
  setIpMinRange: (ipMinRange: string | undefined) => void
  setIpMaxRange: (ipMaxRange: string | undefined) => void
  getFormFieldProps: GetFormFieldPropsFn
  setCustomValidity?: (id: string, validity: boolean) => void
  validateUniqueIpName: (name: string) => void
}

export function BlockIpForm({
  name,
  ip,
  expiration,
  expirationTime,
  isUniqueName,
  ipMinRange,
  ipMaxRange,
  setName,
  setIp,
  setExpiration,
  setExpirationTime,
  setIpMinRange,
  setIpMaxRange,
  getFormFieldProps,
  setCustomValidity,
  validateUniqueIpName
}: BlockIpFormProps) {
  const { formatMessage } = useIntl()

  const minDate = new Date()
  minDate.setHours(23, 59, 0)

  return (
    <>
      <Row>
        <Box basis={8}>
          <TextInput
            data-testid={ipPolicyNameDataTestID}
            label={formatMessage({ id: 'title_name' })}
            placeholder={formatMessage({
              id: 'placeholder_someIpPolicy'
            })}
            value={name}
            onChange={setName}
            maxLength={255}
            required={true}
            type="text"
            {...getFormFieldProps({
              id: 'name',
              getErrorMessage: getNameInputErrorMessages(
                formatMessage({
                  id: 'validation_policyNameRequired'
                }),
                formatMessage({
                  id: 'validation_uniqueIpPolicyName'
                }),
                isUniqueName,
                name
              ),
              onBlur: (e) => {
                validateUniqueIpName(e.target.value)
              },
              validator: ({ validity }) => {
                if (!isUniqueName) {
                  return false
                }
                return validity.valid
              }
            })}
          />
        </Box>
      </Row>
      <Row>
        <Box basis={8}>
          <TextInput
            data-testid={ipBlockDataTestID}
            value={ip}
            onChange={(value) => {
              setIp(value)
              const range = calculateIpRange(value)

              setIpMinRange(range ? range.min : undefined)
              setIpMaxRange(range ? range.max : undefined)
            }}
            placeholder="255.255.255.255"
            renderLabel={() => (
              <Label
                children={formatMessage({
                  id: 'title_ipAddressOrSubnetMask'
                })}
                renderHelpContent={() => (
                  <BasicTooltipContent
                    i18nTitle="title_ipAddressOrSubnetMask"
                    i18nDescription="detail_blockIpAddress"
                  />
                )}
              />
            )}
            maxLength={45}
            required={true}
            type="text"
            {...getFormFieldProps({
              id: 'ip',
              getErrorMessage: getCidrInputErrorMessages(
                formatMessage({
                  id: 'validation_requiredIPAddressSubnetMask'
                }),
                formatMessage({
                  id: 'validation_invalidIPAddressSubnetMask'
                }),
                ip
              ),
              validator: ({ validity }) => {
                if (validity.valid && ip) {
                  const cidr = new IPCIDR(ip)
                  return ip.length > 0 && cidr.isValid()
                }
                return validity.valid
              }
            })}
          />
          {ipMinRange && ipMaxRange && (
            <p
              data-testid={ipRangeDataTestID}
              className={cx(
                paragraphTextCss,
                css`
                  margin-top: 3px;
                `
              )}
            >
              {formatMessage(
                {
                  id: 'variable_matchesIpAddressValidation_react'
                },
                {
                  minRange: ipMinRange,
                  maxRange: ipMaxRange
                }
              )}
            </p>
          )}
        </Box>
        <Box basis={3}>
          <DatePickerDropdown
            labelID="title_expires_date"
            dateOptions={[1, 2, 3, 4, 5]}
            selectedOption={expiration}
            selectedDate={expirationTime}
            onDateOptionSelect={(value) => {
              setExpiration(value)
              setExpirationTime(undefined)

              if (setCustomValidity) {
                setCustomValidity('expirationTime', value !== DateOption.CUSTOM)
              }
            }}
            onCustomDateSelect={(value) => {
              setExpirationTime(value)

              if (setCustomValidity) {
                setCustomValidity('expirationTime', value >= minDate)
              }
            }}
            renderDatePicker={(props) => (
              <DatePicker
                {...props}
                minDate={minDate}
                initialMonth={expirationTime}
                initialDate={expirationTime}
              />
            )}
          />
        </Box>
      </Row>
    </>
  )
}

export const getNameInputErrorMessages =
  (
    requiredMessage: string,
    uniqueMessage: string,
    isUnique: boolean,
    name: string = ''
  ) =>
  ({ validationMessage }) => {
    if (name.length === 0) {
      return requiredMessage
    }
    if (!isUnique) {
      return uniqueMessage
    }
    return validationMessage
  }

export const getCidrInputErrorMessages =
  (
    cidrRequiredMessage: string,
    invalidCidrMessage: string,
    cidrBlock: string = ''
  ) =>
  ({ validationMessage }) => {
    if (cidrBlock.length === 0) {
      return cidrRequiredMessage
    }
    const cidr = new IPCIDR(cidrBlock)
    if (!cidr.isValid()) {
      return invalidCidrMessage
    }
    return validationMessage
  }
